import { CallStateEnum } from '@models/call-state.model.ts';
import {
  CallStateMethodNames,
  CallStateMethods
} from '@shared/store-features/call-state-feature.ts';
import {
  FolderModel,
  MediaModel,
  MoveTreeModel
} from '@models/media-library.model.ts';
import { SortOrder } from '@models/table.model.ts';

export type HandleClose = () => void;
export type SortTuple = [column: string, order: SortOrder];

export type CurrentFolder = {
  id: string;
  title: string;
} & Partial<Omit<FolderModel, 'id' | 'title'>>;

export interface MediaLibraryActionParams {
  data?: any;
  id?: string;
  ids?: string[];
  parentId?: any;
  handleClose?: HandleClose;
}

export interface MediaApiParams {
  id: string;
  page: number;
  limit: number;
  sort: SortTuple;
  filter: Record<string, string[]>;
  search: Record<string, string>;
}

export interface MediaLibraryState extends CallStateMethods {
  search: Record<string, string>;
  filter: Record<string, string[]>;
  sort: SortTuple;
  pagination: {
    page: number;
    rowsPerPage: number;
    totalPages: number;
  };
  callState: CallStateEnum;
  folderCallState: CallStateEnum;
  mediaCallState: CallStateEnum;
  moveTreeCallState: CallStateEnum;
  uploadByUrlCallState: CallStateEnum;
  foldersData: FolderModel[];
  mediaData: MediaModel[];
  moveTreeData: MoveTreeModel[];
  uploadingFiles: any;
  previousBreadcrumb: any;
  updateSearch: (columnId: string, value: string) => void;
  updateFilters: (columnId: string, selectedFilters: string[]) => void;
  updateSort: (columnId: string, order: SortOrder) => void;
  updatePagination: (newPage: number, newRowsPerPage: number) => void;
  resetSearch: () => void;
  getMediaLibraryFolders: (id: any, title?: string) => void;
  getMedia: (id: any) => void;
  getMoveTreeData: () => void;
  uploadFileOrFiles: (data: any) => void;
  refreshCurrentFolder: () => void;
  createFolder: (params: MediaLibraryActionParams) => void;
  moveFolder: (params: MediaLibraryActionParams) => void;
  moveMedia: (params: MediaLibraryActionParams) => void;
  renameMedia: (params: MediaLibraryActionParams) => void;
  renameFolder: (params: MediaLibraryActionParams) => void;
  deleteFolder: (params: MediaLibraryActionParams) => void;
  deleteMedia: (params: MediaLibraryActionParams) => void;
  deleteSingleMedia: (params: MediaLibraryActionParams) => void;

  breadcrumb: { id: string; title: string }[];
  setBreadcrumb: (path: { id: string; title: string }[]) => void;
  goToBreadcrumb: (id: string) => void;

  currentFolder: CurrentFolder | null;
  checkedItems: Record<any, boolean>;
  editingFolder: CurrentFolder | null;
  setCurrentFolder: (folder: any) => void;

  editingMedia: MediaModel | null;
  setEditingMedia: (media: MediaModel | null) => void;
  resetEditingMedia: () => void;

  selectedMedia: Record<string, MediaModel | null>;
  setSelectedMedia: (key: string, media: MediaModel | null) => void;
  resetSelectedMedia: (mediaKey: string) => void;
  resetAllSelectedMedia: () => void;

  toggleItemChecked: (id: any, checked: boolean) => void;
  toggleSelectAll: (checked: boolean) => void;
  resetToggleItemChecked: () => void;

  checkedMediaItems: Record<any, boolean>;
  toggleMediaChecked: (id: any, checked: boolean) => void;
  toggleSelectAllMedia: (checked: boolean) => void;
  resetCheckedMedia: () => void;

  setEditingFolder: (folder: any | null) => void;
  resetMediaLibrary: () => void;
  resetEditingFolder: () => void;
  setSelectedFileFromUrl: (url: string, handleClose?: () => void) => void;

  isFolderLoading: () => boolean;
  isFolderError: () => boolean;
  isFolderLoaded: () => boolean;
  isMediaLoading: () => boolean;
  isMediaError: () => boolean;
  isMediaLoaded: () => boolean;
  isMoveTreeLoading: () => boolean;
  isMoveTreeLoaded: () => boolean;
  isMoveTreeError: () => boolean;
  isUploadByUrlLoading: () => boolean;
  isUploadByUrlLoaded: () => boolean;
  isUploadByUrlError: () => boolean;
}

interface MediaLibraryStateMethods extends CallStateMethodNames {
  getMediaLibraryFolders: 'getMediaLibraryFolders';
  getMedia: 'getMedia';
  getMoveTreeData: 'getMoveTreeData';
  setBreadcrumb: 'setBreadcrumb';
  goToBreadcrumb: 'goToBreadcrumb';
  refreshCurrentFolder: 'refreshCurrentFolder';
  setCurrentFolder: 'setCurrentFolder';
  toggleItemChecked: 'toggleItemChecked';
  toggleSelectAll: 'toggleSelectAll';
  toggleMediaChecked: 'toggleMediaChecked';
  toggleSelectAllMedia: 'toggleSelectAllMedia';
  resetCheckedMedia: 'resetCheckedMedia';
  setEditingFolder: 'setEditingFolder';
  resetEditingFolder: 'resetEditingFolder';
  resetEditingMedia: 'resetEditingMedia';
  setSelectedMedia: 'setSelectedMedia';
  resetSelectedMedia: 'resetSelectedMedia';
  resetAllSelectedMedia: 'resetAllSelectedMedia';
  setSelectedFileFromUrl: 'setSelectedFileFromUrl';
  setEditingMedia: 'setEditingMedia';
  createFolder: 'createFolder';
  renameMedia: 'renameMedia';
  moveFolder: 'moveFolder';
  moveMedia: 'moveMedia';
  deleteMedia: 'deleteMedia';
  deleteSingleMedia: 'deleteSingleMedia';
  renameFolder: 'renameFolder';
  deleteFolder: 'deleteFolder';
  updatePagination: 'updatePagination';
  updateSort: 'updateSort';
  updateFilters: 'updateFilters';
  updateSearch: 'updateSearch';
  resetSearch: 'resetSearch';
  uploadFileOrFiles: 'uploadFileOrFiles';
  resetMediaLibrary: 'resetMediaLibrary';
  resetToggleItemChecked: 'resetToggleItemChecked';
  isFolderLoading: 'isFolderLoading';
  isFolderLoaded: 'isFolderLoaded';
  isFolderError: 'isFolderError';
  isMediaLoading: 'isMediaLoading';
  isMediaLoaded: 'isMediaLoaded';
  isMediaError: 'isMediaError';
  isMoveTreeLoading: 'isMoveTreeLoading';
  isMoveTreeLoaded: 'isMoveTreeLoaded';
  isMoveTreeError: 'isMoveTreeError';
  isUploadByUrlLoading: 'isUploadByUrlLoading';
  isUploadByUrlLoaded: 'isUploadByUrlLoaded';
  isUploadByUrlError: 'isUploadByUrlError';
}

export const MEDIA_LIBRARY_INITIAL_STATE: Omit<
  MediaLibraryState,
  keyof MediaLibraryStateMethods
> = {
  foldersData: [],
  mediaData: [],
  moveTreeData: [],
  previousBreadcrumb: null,
  uploadingFiles: null,
  currentFolder: { id: 'root', title: '/' },
  breadcrumb: [{ id: 'root', title: '/' }],
  checkedItems: {},
  checkedMediaItems: {},
  editingFolder: null,
  editingMedia: null,
  selectedMedia: {},
  search: {},
  filter: {},
  sort: ['createdAt', SortOrder.Descending],
  pagination: { page: 1, rowsPerPage: 12, totalPages: 1 },
  callState: CallStateEnum.Init,
  mediaCallState: CallStateEnum.Init,
  folderCallState: CallStateEnum.Init,
  moveTreeCallState: CallStateEnum.Init,
  uploadByUrlCallState: CallStateEnum.Init
};
